import * as i0 from '@angular/core';
import { Injectable, Component, NgModule } from '@angular/core';
import { BrowserCacheLocation, LogLevel, EventType } from '@azure/msal-browser';
import { from } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import * as i1 from '@azure/msal-angular';
import * as i3 from '@angular/router';
import { CommonModule } from '@angular/common';

class ObjectHelpers {
    static isNull(obj) {
        return obj === null;
    }
    static isNullish(obj) {
        return this.isNullOrUndefined(obj);
    }
    static isNotNull(obj) {
        return !this.isNull(obj);
    }
    static isEmpty(obj) {
        let isEmpty = false;
        if (typeof obj === 'object') { // arrays and objects
            isEmpty = Object.keys(obj).length ? false : true;
        }
        return isEmpty;
    }
    static isNotEmpty(obj) {
        return !this.isEmpty(obj);
    }
    // Undefined is considered 'Null' here due to historical usage
    static isNullOrEmpty(obj) {
        return this.isNullOrUndefined(obj) || this.isEmpty(obj);
    }
    static isNotNullOrEmpty(obj) {
        return !this.isNullOrEmpty(obj);
    }
    static isNotNullish(obj) {
        return !this.isNullish(obj);
    }
    static isUndefined(obj) {
        return typeof obj === 'undefined';
    }
    static isNotUndefined(obj) {
        return !this.isUndefined(obj);
    }
    static isNullOrUndefined(obj) {
        return this.isNull(obj) || this.isUndefined(obj);
    }
    static isNotNullOrUndefined(obj) {
        return !this.isNullOrUndefined(obj);
    }
    static equals(obj1, obj2) {
        return (obj1 === obj2);
    }
    static clone(obj) {
        if (this.isNullish(obj)) {
            return null;
        }
        // Object assign does not work for deep cloning!
        return JSON.parse(JSON.stringify(obj));
    }
}

/**
 * This file contains authentication parameters. Contents of this file
 * is roughly the same across other MSAL.js libraries. These parameters
 * are used to initialize Angular and MSAL Angular configurations in
 * in app.module.ts file.
 */
// TODO:MFA
const isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;
/**
 * Enter here the user flows and custom policies for your B2C application,
 * To learn more about user flows, visit https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies,
 * visit https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
const b2cPolicies = {
    names: {
        signUpSignIn: 'B2C_1A_SIGNUP_SIGNIN',
        editProfile: 'b2c_1_edit_profile_v2'
    },
    authorities: {
        signUpSignIn: {
            authority: 'https://{{tenantId}}.b2clogin.com/{{tenantId}}.onmicrosoft.com/{{policy}}'
        },
        editProfile: {
            authority: 'https://{{tenantId}}.b2clogin.com/{{tenantId}}.onmicrosoft.com/b2c_1_edit_profile_v2'
        },
        generalRedirect: {
            authority: 'https://{{tenantId}}.b2clogin.com/{{tenantId}}.onmicrosoft.com/{{policy}}'
        }
    },
    authorityDomain: '{{tenantId}}.b2clogin.com'
};
/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
const msalConfig = {
    auth: {
        clientId: '',
        authority: b2cPolicies.authorities.signUpSignIn.authority,
        knownAuthorities: [b2cPolicies.authorityDomain],
        redirectUri: '/auth',
        postLogoutRedirectUri: '/',
        // If "true", will navigate back to the original request location before processing the auth code response.
        navigateToLoginRequestUrl: false,
    },
    cache: {
        // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
        cacheLocation: BrowserCacheLocation.SessionStorage,
        storeAuthStateInCookie: isIE, // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: {
        loggerOptions: {
            loggerCallback(logLevel, message) {
                console.log(message);
            },
            logLevel: LogLevel.Verbose,
            piiLoggingEnabled: false
        },
        allowRedirectInIframe: true
    }
};
/**
 * An optional silentRequest object can be used to achieve silent SSO
 * between applications by providing a "login_hint" property.
 */
const silentRequest = {
    scopes: ['https://{{tenantId}}.onmicrosoft.com/VfService/basicAccess'],
    forceRefresh: true
};

/* eslint-disable prefer-for-of */
class IfB2CConfigService {
    static getConfig(environmentConfig = null) {
        if (ObjectHelpers.isNullish(environmentConfig) || ObjectHelpers.isNotNullish(this.msalConfigCache)) {
            return this.msalConfigCache;
        }
        this.processEnvironmentConfigCache(environmentConfig);
        const envB2CConfig = this.environmentConfigCache.b2cConfig;
        msalConfig.auth.clientId = envB2CConfig.clientId;
        const tenantDomain = b2cPolicies.authorityDomain.replace('{{tenantId}}', envB2CConfig.tenantId);
        msalConfig.auth.knownAuthorities = [tenantDomain];
        const policy = envB2CConfig.loginPolicy ? envB2CConfig.loginPolicy : b2cPolicies.names.signUpSignIn;
        msalConfig.auth.authority = b2cPolicies.authorities.signUpSignIn.authority.split('{{tenantId}}').join(envB2CConfig.tenantId)
            .replace('{{policy}}', policy);
        msalConfig.system.loggerOptions.logLevel = IfB2CConfigService.environmentsForVerboseLogging.includes(this.environmentConfigCache.env)
            ? LogLevel.Verbose
            : LogLevel.Warning;
        if (envB2CConfig.redirectUri) {
            msalConfig.auth.redirectUri = envB2CConfig.redirectUri;
        }
        if (envB2CConfig.postLogoutRedirectUri) {
            msalConfig.auth.postLogoutRedirectUri = envB2CConfig.postLogoutRedirectUri;
        }
        this.msalConfigCache = msalConfig;
        return msalConfig;
    }
    static getSilentRequest(environmentConfig = null) {
        const newSilentRequest = Object.assign({}, silentRequest);
        this.processEnvironmentConfigCache(environmentConfig);
        const envB2CConfig = this.environmentConfigCache.b2cConfig;
        for (let i = 0; i < newSilentRequest.scopes.length; i++) {
            newSilentRequest.scopes[i] = newSilentRequest.scopes[i].replace('{{tenantId}}', envB2CConfig.tenantId);
        }
        const policyCache = this.getFromSession(this.policyCacheKey);
        if (policyCache) {
            newSilentRequest.authority = this.getRedirectAuthority(policyCache);
        }
        return newSilentRequest;
    }
    static persistPolicy(policy) {
        this.storeInSession(this.policyCacheKey, policy);
    }
    // Clear policy cache and start using default policy from config
    static clearPolicy() {
        this.removeFromSession(this.policyCacheKey);
    }
    static getRedirectAuthority(policy) {
        const authorityFromConfig = b2cPolicies.authorities.generalRedirect.authority;
        const envB2CConfig = this.environmentConfigCache.b2cConfig;
        const authority = authorityFromConfig.split('{{tenantId}}').join(envB2CConfig.tenantId).replace('{{policy}}', policy);
        return authority;
    }
    static processEnvironmentConfigCache(environmentConfig) {
        if (ObjectHelpers.isNotNullish(environmentConfig)) {
            this.environmentConfigCache = environmentConfig;
        }
    }
    static storeInSession(key, payload) {
        window.sessionStorage.setItem(key, payload);
    }
    static getFromSession(key) {
        return window.sessionStorage.getItem(key);
    }
    static removeFromSession(key) {
        return window.sessionStorage.removeItem(key);
    }
}
IfB2CConfigService.environmentsForVerboseLogging = ['local', 'QA:Main'];
IfB2CConfigService.environmentConfigCache = null;
IfB2CConfigService.msalConfigCache = null;
IfB2CConfigService.policyCacheKey = 'if-security-policy';
IfB2CConfigService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: IfB2CConfigService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
IfB2CConfigService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: IfB2CConfigService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: IfB2CConfigService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

// MFA:TODO This class is an early thought on the structure of this shared library
// This is not complete, not final, and we have serveral future stories to build out the features
// of this package
class IfAuthService {
    constructor(msalAuthService, msalBroadcastService) {
        this.msalAuthService = msalAuthService;
        this.msalBroadcastService = msalBroadcastService;
    }
    initService(initOptions) {
        console.log('setupEventHandler');
        this.initOptions = initOptions;
        this.msalBroadcastService.msalSubject$
            .pipe(filter((msg) => msg.eventType === EventType.HANDLE_REDIRECT_END), takeUntil(this.initOptions.appDestroyedSubject))
            .subscribe((result) => {
            console.log('msalBroadcastService event', result);
            // do something with the result, such as accessing ID token
            const payload = result.payload;
            const msalInstance = this.msalAuthService.instance;
            const accounts = msalInstance.getAllAccounts();
            if (accounts.length > 0) {
                const account = accounts[0];
                console.log('setupEventHandler 2', account);
                msalInstance.setActiveAccount(account);
                this.acquireAccessTokenFromB2C().then(tokenResult => {
                    const userInfo = this.createUserLoginDataFromAuthResult(tokenResult);
                    initOptions.onLoginCallback(userInfo);
                });
            }
        });
    }
    // TODO:MFA:POL
    loginRedirect(redirectParams) {
        const msalInstance = this.msalAuthService.instance;
        if (redirectParams.shouldPersistPolicy) {
            IfB2CConfigService.persistPolicy(redirectParams.policy);
        }
        const redirectAuthority = IfB2CConfigService.getRedirectAuthority(redirectParams.policy);
        const redirectRequest = {
            authority: redirectAuthority,
            scopes: [],
            redirectUri: redirectParams.redirectUri,
            extraQueryParameters: redirectParams.extraQueryParameters
        };
        msalInstance.handleRedirectPromise()
            .then((tokenResponse) => {
            if (!tokenResponse) {
                msalInstance.loginRedirect(redirectRequest);
            }
            else {
                // Do something with the tokenResponse
                console.log('b2clogin-2');
            }
        })
            .catch(err => {
            // Handle error
            console.error(err);
        });
    }
    // TODO:MFA
    loginPopup() {
        const msalInstance = this.msalAuthService.instance;
        // NOTE: For customization, call loginPopup with a PopupRequest : popupWindowAttributes
        msalInstance.loginPopup().then((loginResponse) => {
            const account = loginResponse.account;
            msalInstance.setActiveAccount(account);
            this.acquireAccessTokenFromB2C().then(tokenResult => {
                const userInfo = this.createUserLoginDataFromAuthResult(tokenResult);
                this.initOptions.onLoginCallback(userInfo);
            });
        })
            .catch(error => {
            // login failure
            console.log(error);
        });
    }
    shouldRedirectToLoginScreen() {
        const shouldRedirectToLoginScreen = !this.initOptions.isLoggedInInApp();
        return shouldRedirectToLoginScreen;
    }
    getAccessToken() {
        const aquireTokenPromise = this.acquireAccessTokenFromB2C().then(result => {
            const userInfo = this.createUserLoginDataFromAuthResult(result);
            return userInfo;
        });
        return from(aquireTokenPromise);
    }
    // TODO:MFA
    acquireAccessTokenFromB2C() {
        const msalInstance = this.msalAuthService.instance;
        const tokenRequest = IfB2CConfigService.getSilentRequest();
        // Typically don't need caching or throttling as the acquireTokenSilent calls already provides that.
        return msalInstance.acquireTokenSilent(tokenRequest)
            .then(response => {
            // get access token from response
            console.log('accessToken', response.accessToken);
            return response;
        })
            .catch(err => {
            // could also check if err instance of InteractionRequiredAuthError if you can import the class.
            if (err.name === 'InteractionRequiredAuthError') {
                return msalInstance.acquireTokenPopup(tokenRequest)
                    .then(response => {
                    // get access token from response
                    console.log('accessToken', response.accessToken);
                    return response;
                })
                    .catch(popupError => {
                    console.log('acquireTokenPopup error', popupError);
                    return null;
                });
            }
            console.log('acquireTokenSilent error', err);
            return null;
        });
    }
    // TODO:MFA - Need more work with ticket 74718 74719
    createUserLoginDataFromAuthResult(result) {
        const userloginData = {
            accessToken: result.accessToken,
            refreshToken: result.accessToken,
            userName: result.account.username,
            expires: result.expiresOn
        };
        if (result.account.idTokenClaims) {
            const giveNameClaims = result.account.idTokenClaims.given_name;
            userloginData.firstName = ObjectHelpers.isNotNullOrEmpty(giveNameClaims) ? String(giveNameClaims) : '';
            const familyNameClaims = result.account.idTokenClaims.family_name;
            userloginData.lastName = ObjectHelpers.isNotNullOrEmpty(familyNameClaims) ? String(familyNameClaims) : '';
            userloginData.roles = result.account.idTokenClaims.roles;
        }
        return userloginData;
    }
}
IfAuthService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: IfAuthService, deps: [{ token: i1.MsalService }, { token: i1.MsalBroadcastService }], target: i0.ɵɵFactoryTarget.Injectable });
IfAuthService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: IfAuthService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: IfAuthService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.MsalService }, { type: i1.MsalBroadcastService }]; } });

class B2CLoginComponent {
    constructor(msalAuthService, ifAuthService, router) {
        this.msalAuthService = msalAuthService;
        this.ifAuthService = ifAuthService;
        this.router = router;
    }
    // MFA:TODO This class is an early thought on the structure of this shared library
    // This is not complete, not final, and we have serveral future stories to build out the features
    // of this package
    ngOnInit() {
        if (!this.ifAuthService.shouldRedirectToLoginScreen()) {
            return;
        }
        const msalInstance = this.msalAuthService.instance;
        // Have resolve the promise/Oservable
        msalInstance.handleRedirectPromise()
            .then((tokenResponse) => {
            if (!tokenResponse) {
                msalInstance.loginRedirect();
            }
            else {
                // Do something with the tokenResponse
                console.log('b2clogin-2');
            }
        })
            .catch(err => {
            // Handle error
            console.error(err);
        });
    }
}
B2CLoginComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: B2CLoginComponent, deps: [{ token: i1.MsalService }, { token: IfAuthService }, { token: i3.Router }], target: i0.ɵɵFactoryTarget.Component });
B2CLoginComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: B2CLoginComponent, selector: "if-b2c-login", ngImport: i0, template: "<div class=\"b2c-int-container\">\n</div>\n", styles: [".b2c-int-container{width:100%;height:100vh;background-color:#fff;position:fixed;top:0;left:0;z-index:999}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: B2CLoginComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'if-b2c-login',
                    templateUrl: './b2c-login.component.html',
                    styleUrls: ['./b2c-login.component.css']
                }]
        }], ctorParameters: function () { return [{ type: i1.MsalService }, { type: IfAuthService }, { type: i3.Router }]; } });

class TokenAuthComponent {
    constructor(msalAuthService, msalBroadcastService, ifAuthService) {
        this.msalAuthService = msalAuthService;
        this.msalBroadcastService = msalBroadcastService;
        this.ifAuthService = ifAuthService;
    }
    ngOnInit() {
        console.log('token-auth 1');
        const accounts = this.msalAuthService.instance.getAllAccounts();
        if (accounts.length > 0) {
            const account = accounts[0];
            console.log('token-auth 2', account);
        }
    }
}
TokenAuthComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TokenAuthComponent, deps: [{ token: i1.MsalService }, { token: i1.MsalBroadcastService }, { token: IfAuthService }], target: i0.ɵɵFactoryTarget.Component });
TokenAuthComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: TokenAuthComponent, selector: "if-token-auth", ngImport: i0, template: "<div class=\"b2c-int-container\">\n</div>\n", styles: [".b2c-int-container{width:100%;height:100vh;background-color:#fff;position:fixed;top:0;left:0;z-index:999}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TokenAuthComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'if-token-auth',
                    templateUrl: './token-auth.component.html',
                    styleUrls: ['./token-auth.component.css']
                }]
        }], ctorParameters: function () { return [{ type: i1.MsalService }, { type: i1.MsalBroadcastService }, { type: IfAuthService }]; } });

class IfAngularSecurityModule {
}
IfAngularSecurityModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: IfAngularSecurityModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
IfAngularSecurityModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: IfAngularSecurityModule, declarations: [TokenAuthComponent,
        B2CLoginComponent], imports: [CommonModule], exports: [TokenAuthComponent, B2CLoginComponent] });
IfAngularSecurityModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: IfAngularSecurityModule, providers: [IfAuthService,
        IfB2CConfigService], imports: [[
            CommonModule
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: IfAngularSecurityModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [TokenAuthComponent,
                        B2CLoginComponent],
                    providers: [IfAuthService,
                        IfB2CConfigService],
                    imports: [
                        CommonModule
                    ],
                    exports: [TokenAuthComponent, B2CLoginComponent]
                }]
        }] });

/*
 * Public API Surface of if-angular-security
 */

/**
 * Generated bundle index. Do not edit.
 */

export { B2CLoginComponent, IfAngularSecurityModule, IfAuthService, IfB2CConfigService, TokenAuthComponent };
